/**
 * Market Data Fallback Service
 *
 * Provides intelligent fallbacks when market APIs are unavailable:
 * - Cached values with timestamps
 * - Approximated values based on portfolio data
 * - Default safe values
 */

import { logger } from './logger';
import { API_CONFIG } from '../constants';
import type { FearGreedIndex, VolatilityMetrics, MomentumIndicators } from './marketAPI';

interface CachedData<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

const CACHE_DURATION = API_CONFIG.CACHE_TTL_MS; // Cache TTL from constants
const CACHE_STORAGE_KEY = 'market_data_cache';

class MarketFallbackService {
  private cache: Map<string, CachedData<any>> = new Map();

  constructor() {
    // Load cache from localStorage on initialization
    this.loadCacheFromStorage();
  }

  /**
   * Load cache from localStorage
   */
  private loadCacheFromStorage() {
    try {
      const stored = localStorage.getItem(CACHE_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        Object.entries(parsed).forEach(([key, value]) => {
          this.cache.set(key, value as CachedData<any>);
        });
        logger.info('MarketFallback', 'Loaded cache from localStorage');
      }
    } catch (error) {
      logger.error('MarketFallback', 'Failed to load cache from localStorage', error);
    }
  }

  /**
   * Save cache to localStorage
   */
  private saveCacheToStorage() {
    try {
      const cacheObj: Record<string, CachedData<any>> = {};
      this.cache.forEach((value, key) => {
        cacheObj[key] = value;
      });
      localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(cacheObj));
    } catch (error) {
      logger.error('MarketFallback', 'Failed to save cache to localStorage', error);
    }
  }

  /**
   * Store data in cache
   */
  cacheData<T>(key: string, data: T): void {
    const now = Date.now();
    const cachedData: CachedData<T> = {
      data,
      timestamp: now,
      expiresAt: now + CACHE_DURATION
    };

    this.cache.set(key, cachedData);
    this.saveCacheToStorage();
  }

  /**
   * Get cached data if available and not expired
   */
  getCachedData<T>(key: string): T | null {
    const cached = this.cache.get(key);

    if (!cached) {
      return null;
    }

    const now = Date.now();
    if (now > cached.expiresAt) {
      // Cache expired
      this.cache.delete(key);
      this.saveCacheToStorage();
      return null;
    }

    logger.info('MarketFallback', `Using cached data for ${key}`, {
      age: Math.round((now - cached.timestamp) / 1000) + 's'
    });

    return cached.data as T;
  }

  /**
   * Intelligent fallback for Fear & Greed Index
   *
   * Fallback strategy:
   * 1. Try cached value (< 15 min old)
   * 2. Use neutral value (50) with warning
   */
  getFearGreedFallback(): FearGreedIndex {
    const cached = this.getCachedData<FearGreedIndex>('fear-greed');

    if (cached) {
      logger.info('MarketFallback', 'Using cached Fear & Greed Index');
      return cached;
    }

    // Return neutral value as safe default
    logger.warn('MarketFallback', 'Using default neutral Fear & Greed Index (50)');
    return {
      value: 50,
      classification: 'Neutral',
      timestamp: Date.now(),
      timeUntilUpdate: 0
    };
  }

  /**
   * Intelligent fallback for Volatility Metrics
   *
   * Fallback strategy:
   * 1. Try cached value
   * 2. Use moderate volatility (50) as default
   */
  getVolatilityFallback(symbol: string): VolatilityMetrics {
    const cacheKey = `volatility-${symbol}`;
    const cached = this.getCachedData<VolatilityMetrics>(cacheKey);

    if (cached) {
      logger.info('MarketFallback', `Using cached volatility for ${symbol}`);
      return cached;
    }

    // Return moderate volatility as safe default
    logger.warn('MarketFallback', `Using default volatility for ${symbol}`);
    return {
      symbol,
      volatilityIndex: 50,
      metrics: {
        atr: 0,
        atrPercent: 0,
        bollingerBandWidth: 0
      },
      classification: 'Moderate'
    };
  }

  /**
   * Intelligent fallback for Momentum Indicators
   *
   * Fallback strategy:
   * 1. Try cached value
   * 2. Use neutral momentum (50) as default
   */
  getMomentumFallback(symbol: string): MomentumIndicators {
    const cacheKey = `momentum-${symbol}`;
    const cached = this.getCachedData<MomentumIndicators>(cacheKey);

    if (cached) {
      logger.info('MarketFallback', `Using cached momentum for ${symbol}`);
      return cached;
    }

    // Return neutral momentum as safe default
    logger.warn('MarketFallback', `Using default momentum for ${symbol}`);
    return {
      symbol,
      momentumIndex: 50,
      metrics: {
        rsi: 50,
        macd: null
      },
      classification: 'Neutral'
    };
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.cache.clear();
    localStorage.removeItem(CACHE_STORAGE_KEY);
    logger.info('MarketFallback', 'Cache cleared');
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    totalEntries: number;
    entries: Array<{ key: string; age: number; expiresIn: number }>;
  } {
    const now = Date.now();
    const entries: Array<{ key: string; age: number; expiresIn: number }> = [];

    this.cache.forEach((value, key) => {
      entries.push({
        key,
        age: Math.round((now - value.timestamp) / 1000),
        expiresIn: Math.round((value.expiresAt - now) / 1000)
      });
    });

    return {
      totalEntries: this.cache.size,
      entries
    };
  }
}

// Global instance
export const marketFallback = new MarketFallbackService();
